import React, { useState } from "react";
import {
  Box, Paper, Typography, Button, Stack, TextField, ToggleButton, ToggleButtonGroup,
  Table, TableHead, TableRow, TableCell, TableBody, Chip, Alert, Divider,
} from "@mui/material";
import { getDecision, getStatus, DecisionResult, StatusResult } from "../api";

const nowIso = () => new Date().toISOString().slice(0, 19) + "Z";

function TriggerChip({ t }: { t: string }) {
  const color = t === "SIGNAL" ? "secondary" : t === "TIME" ? "primary" : "default";
  return <Chip size="small" color={color as any} label={t} />;
}

function DecisionView({ d }: { d: DecisionResult }) {
  return (
    <Stack gap={2}>
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>Decision</Typography>
        {d.decision ? (
          <Stack gap={0.5}>
            <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
              <TriggerChip t={d.decision.trigger} />
              {d.decision.actions.map((a) => <Chip key={a} size="small" variant="outlined" label={a} />)}
            </Stack>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              audience <b>{d.decision.audienceName}</b> · matchTime {d.decision.matchTime} · duration {d.decision.duration}
            </Typography>
            <Typography variant="caption" sx={{ fontFamily: "monospace", opacity: 0.7 }}>
              {d.decision.mediaPoint} → {d.decision.policy} → {d.decision.viewingPolicy} → {d.decision.audience}
            </Typography>
          </Stack>
        ) : (
          <Alert severity="info">No applicable policy at this time/audience (the distributor may serve any audience).</Alert>
        )}
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>Active policy stack ({d.active.length})</Typography>
        {d.active.length === 0 ? (
          <Typography variant="body2" sx={{ opacity: 0.6 }}>none active</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>trigger</TableCell><TableCell>actions</TableCell>
                <TableCell>priority</TableCell><TableCell>matchTime</TableCell><TableCell>mediaPoint</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {d.active.map((a, i) => (
                <TableRow key={i} sx={i === 0 ? { bgcolor: "action.hover" } : undefined}>
                  <TableCell><TriggerChip t={a.trigger} /></TableCell>
                  <TableCell>{a.actions.join(", ")}</TableCell>
                  <TableCell>{a.priority ?? "—"}</TableCell>
                  <TableCell>{a.matchTime}</TableCell>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{a.mediaPoint}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {d.signal && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>Synthesized SCTE-35 signal</Typography>
          <Box component="pre" sx={{ m: 0, p: 1, bgcolor: "action.hover", borderRadius: 1, overflowX: "auto", fontSize: 12 }}>
            {d.signal}
          </Box>
        </Paper>
      )}
      <Typography variant="caption" sx={{ opacity: 0.6 }}>audit: {d.auditId}</Typography>
    </Stack>
  );
}

function StatusView({ s }: { s: StatusResult }) {
  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" gap={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1">Status @ {s.at}</Typography>
        <Chip size="small" color={s.active ? "success" : "default"} label={s.active ? "ACTIVE" : "INACTIVE"} />
      </Stack>
      {s.mediaPoints.length === 0 ? (
        <Typography variant="body2" sx={{ opacity: 0.6 }}>no active MediaPoints</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow><TableCell>mediaPoint</TableCell><TableCell>matchTime</TableCell><TableCell>duration</TableCell></TableRow>
          </TableHead>
          <TableBody>
            {s.mediaPoints.map((m) => (
              <TableRow key={m.mediaPoint}>
                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{m.mediaPoint}</TableCell>
                <TableCell>{m.matchTime}</TableCell><TableCell>{m.duration}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Typography variant="caption" sx={{ opacity: 0.6 }}>audit: {s.auditId}</Typography>
    </Paper>
  );
}

export default function DecisionTester() {
  const [mode, setMode] = useState<"decision" | "status">("decision");
  const [source, setSource] = useState("cnn");
  const [eventtime, setEventtime] = useState(nowIso());
  const [audiencetype, setAudiencetype] = useState("urn:scte:224:audience:mvpd");
  const [audiencevalue, setAudiencevalue] = useState("comcast");
  const [upidtype, setUpidtype] = useState("");
  const [signalid, setSignalid] = useState("");
  const [dec, setDec] = useState<DecisionResult | null>(null);
  const [stat, setStat] = useState<StatusResult | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setErr(null); setBusy(true); setDec(null); setStat(null);
    try {
      if (mode === "decision") {
        setDec(await getDecision({
          source, eventtime, audiencetype, audiencevalue,
          segmentationupidtype: upidtype || undefined, signalid: signalid || undefined,
        }));
      } else {
        setStat(await getStatus({ source, at: eventtime }));
      }
    } catch (e: any) {
      setErr(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: "auto" }}>
      <Typography variant="h5" gutterBottom>Decision Tester</Typography>
      <Typography variant="body2" sx={{ opacity: 0.7, mb: 2 }}>
        Run a decision (or status) query against the live SCTE-224 graph. Supplying
        segmentationupidtype + signalid synthesizes an SCTE-35 trigger and exercises MatchSignal matching.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <ToggleButtonGroup exclusive size="small" value={mode} onChange={(_, v) => v && setMode(v)} sx={{ mb: 2 }}>
          <ToggleButton value="decision">Decision</ToggleButton>
          <ToggleButton value="status">Status</ToggleButton>
        </ToggleButtonGroup>
        <Stack direction={{ xs: "column", sm: "row" }} gap={2} flexWrap="wrap">
          <TextField size="small" label="source (channel)" value={source} onChange={(e) => setSource(e.target.value)} />
          <TextField size="small" label={mode === "decision" ? "eventtime" : "at (instant)"} value={eventtime}
            onChange={(e) => setEventtime(e.target.value)} sx={{ minWidth: 230 }} />
          {mode === "decision" && (
            <>
              <TextField size="small" label="audiencetype" value={audiencetype}
                onChange={(e) => setAudiencetype(e.target.value)} sx={{ minWidth: 250 }} />
              <TextField size="small" label="audiencevalue" value={audiencevalue} onChange={(e) => setAudiencevalue(e.target.value)} />
              <TextField size="small" label="segmentationupidtype (opt)" value={upidtype} onChange={(e) => setUpidtype(e.target.value)} />
              <TextField size="small" label="signalid (opt)" value={signalid} onChange={(e) => setSignalid(e.target.value)} />
            </>
          )}
        </Stack>
        <Button variant="contained" sx={{ mt: 2 }} onClick={run} disabled={busy}>
          {busy ? "Running…" : "Run"}
        </Button>
      </Paper>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
      <Divider sx={{ mb: 2 }} />
      {dec && <DecisionView d={dec} />}
      {stat && <StatusView s={stat} />}
    </Box>
  );
}
